import React, { useState, useEffect } from 'react';
import { colord } from 'colord';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './../common/SafeIcon';

const ColorPicker = ({ color, onChange }) => {
  const [hexInput, setHexInput] = useState(color);

  useEffect(() => {
    setHexInput(color);
  }, [color]);

  const handleHexChange = (e) => {
    let val = e.target.value;
    if (!val.startsWith('#')) val = '#' + val;
    setHexInput(val);
    if (colord(val).isValid()) {
      onChange(colord(val).toHex());
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const hsl = colord(color).toHsl();
  const rgb = colord(color).toRgb();

  return (
    <div className="glass-panel p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl md:rounded-3xl shadow-xl border-2 border-white/20">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 sm:mb-5">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/[0.06] border border-white/25 flex items-center justify-center flex-shrink-0 shadow-lg">
          <SafeIcon icon={FiIcons.FiDroplet} className="text-blue-400 text-lg sm:text-xl" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm sm:text-base md:text-lg font-bold text-white uppercase tracking-wide font-display">
            Color Picker
          </h3>
          <p className="text-[9px] sm:text-[10px] text-white/50 uppercase tracking-wider font-display">
            Start Your Analysis
          </p>
        </div>
      </div>

      {/* Interactive Color Picker */}
      <div className="relative group w-full aspect-square rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl border-2 border-white/30 mb-4 sm:mb-5">
        <div
          className="absolute inset-0 transition-colors duration-500"
          style={{ backgroundColor: color }}
        />
        <input
          type="color"
          value={color}
          onChange={(e) => onChange(e.target.value)}
          className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <SafeIcon 
              icon={FiIcons.FiEyedropper} 
              className="text-white/80 text-4xl sm:text-5xl md:text-6xl mb-2 drop-shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
            />
            <p className="text-xs sm:text-sm font-bold text-white/90 uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg">
              Click to Change
            </p>
          </div>
        </div>
      </div>

      {/* Current Color Display */}
      <div className="flex items-center justify-between gap-3 p-3 sm:p-4 bg-white/[0.05] rounded-xl border border-white/15 mb-4 sm:mb-5">
        <div className="min-w-0 flex-1">
          <p className="text-[9px] sm:text-[10px] text-white/50 uppercase tracking-wider mb-1 font-display">
            Current Color
          </p>
          <p className="text-lg sm:text-xl md:text-2xl font-bold text-white tracking-tight truncate font-mono">
            {color.toUpperCase()}
          </p>
        </div>
        <button
          onClick={() => copyToClipboard(color)}
          className="p-3 sm:p-3.5 bg-white/[0.06] hover:bg-white/[0.12] rounded-lg sm:rounded-xl transition-all text-gray-300 hover:text-white border border-white/15 group flex-shrink-0 shadow-lg active:scale-95"
        >
          <SafeIcon icon={FiIcons.FiCopy} className="group-active:scale-90 transition-transform text-base sm:text-lg" />
        </button>
      </div>

      {/* Hex Input */}
      <div className="space-y-2 sm:space-y-2.5 mb-4 sm:mb-5">
        <label className="text-[9px] sm:text-[10px] text-white/50 uppercase font-bold tracking-wider ml-1 font-display">
          Hex Input
        </label>
        <div className="relative">
          <span className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-white/70 font-mono text-base sm:text-lg font-bold">
            #
          </span>
          <input
            type="text"
            value={hexInput.replace('#', '')}
            onChange={handleHexChange}
            className="input-base w-full pl-8 sm:pl-10 uppercase font-mono tracking-widest text-base sm:text-lg h-12 sm:h-14 font-bold"
            maxLength={6}
            placeholder="3B82F6"
          />
        </div>
      </div>

      {/* RGB & HSL Values */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <div className="p-3 sm:p-4 rounded-xl bg-white/[0.05] border border-white/15 shadow-lg">
          <div className="flex items-center gap-2 mb-2">
            <SafeIcon icon={FiIcons.FiGrid} className="text-purple-400 text-xs sm:text-sm" />
            <span className="text-[9px] sm:text-[10px] text-white/50 uppercase font-bold tracking-wider font-display">
              RGB
            </span>
          </div>
          <p className="text-[10px] sm:text-xs font-mono text-white/90 truncate font-medium">
            {rgb.r}, {rgb.g}, {rgb.b}
          </p>
        </div>
        <div className="p-3 sm:p-4 rounded-xl bg-white/[0.05] border border-white/15 shadow-lg">
          <div className="flex items-center gap-2 mb-2">
            <SafeIcon icon={FiIcons.FiCircle} className="text-blue-400 text-xs sm:text-sm" />
            <span className="text-[9px] sm:text-[10px] text-white/50 uppercase font-bold tracking-wider font-display">
              HSL
            </span>
          </div>
          <p className="text-[10px] sm:text-xs font-mono text-white/90 truncate font-medium">
            {Math.round(hsl.h)}°, {Math.round(hsl.s)}%, {Math.round(hsl.l)}%
          </p>
        </div>
      </div>

      {/* Quick Tip */}
      <div className="mt-4 sm:mt-5 p-3 sm:p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <div className="flex items-start gap-2 sm:gap-3">
          <SafeIcon icon={FiIcons.FiInfo} className="text-blue-400 mt-0.5 flex-shrink-0 text-xs sm:text-sm" />
          <p className="text-[9px] sm:text-[10px] text-blue-200/90 leading-relaxed">
            <strong className="font-bold">Quick Start:</strong> Click the color square above or paste a hex code to begin your brand color intelligence analysis.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ColorPicker;